import Phaser from 'phaser';
import { Entity } from '@/entities/Entity';
import type { HeroDef } from '@/data/heroes';
import type { InputManager } from '@/systems/InputManager';
import type { PlayerId } from '@/systems/InputBindings';
import {
  Hitbox,
  type Combatant,
  type CombatSystem,
  type HitSpec,
  type Team,
} from '@/systems/Combat';
import { boxFromCenter } from '@/systems/box';
import {
  createVehicleState,
  mountVehicle,
  vehicleReceiveHit,
  type VehicleDef,
  type VehicleState,
} from '@/systems/VehicleState';
import type { Vehicle } from '@/entities/Vehicle';
import { ComboTracker } from '@/systems/ComboTracker';
import { HEAVY_CHARGE_FRAMES, type MoveDef } from '@/data/moves';
import { DEFAULT_THROW_SPEC, COMMAND_THROW_SPEC, type ThrowSpec } from '@/data/throws';
import { resolveThrowType, createThrownState } from '@/systems/ThrowSystem';
import {
  createBlockParryState,
  tickBlockParry,
  notifyBlockTap,
  resolveBlock,
  isInCounterWindow,
  applyCounterBonus,
  SCREEN_FREEZE_FRAMES,
  type BlockParryState,
} from '@/systems/BlockParry';
import { createStarMeter, consumeStar, type StarMeterState } from '@/systems/StarMeter';
import { getHeroSuper, superToHitSpec, SUPER_FREEZE_FRAMES, type SuperDef } from '@/data/supers';
import {
  createGreenHealthState,
  beginGreenWindow,
  tickGreenHealth,
  notifyHitLanded,
  lockInGreenLoss,
  SPECIAL_HP_COST,
  superSelfDamageCost,
  type GreenHealthState,
} from '@/systems/GreenHealthRecovery';
import type { WeaponDef } from '@/data/weapons';
import { WEAPON_THROW_RANGE } from '@/data/weapons';
import {
  createWeaponState,
  applyWeaponDamage,
  useWeapon,
  effectiveReach,
} from '@/systems/WeaponSystem';
import {
  createDodgeRollState,
  beginDodgeRoll,
  tickDodgeRoll,
  canDodge,
  isDodgeInvulnerable,
  DODGE_SPEED,
  type DodgeRollState,
} from '@/systems/DodgeRoll';
import { MotionInputBuffer, dirsToDir } from '@/systems/MotionInput';
import { getHeroSpecials, type SpecialDef } from '@/data/specials';
import {
  createDashState,
  tickDash,
  cancelDash,
  resolveAttackContext,
  type DashState,
} from '@/systems/CommandInterpreter';
import { DASH_ATTACK, JUMP_ATTACK, BACK_ATTACK } from '@/data/moves';
import { TauntSystem } from '@/systems/TauntSystem';
import { addStar } from '@/systems/StarMeter';
import { resolveCommandSpecial, type CommandTable } from '@/systems/CommandSpecial';
import {
  RAY_COMMAND_TABLE,
  RAY_PILEDRIVER_ID,
  RAY_PILEDRIVER_WHIFF_RECOVERY,
  RAY_PILEDRIVER_HIT_RECOVERY,
  ERROL_COMMAND_TABLE,
  ERROL_BOUNDARY_SMASH_ID,
  ERROL_CREASE_SWEEP_ID,
  ERROL_CREASE_SWEEP_RECOVERY,
  ERROL_BOUNDARY_SMASH_PROJ_DEF,
  KRIS_COMMAND_TABLE,
  KRIS_HAMMER_THROW_ID,
  KRIS_GROUND_ZERO_ID,
  KRIS_GROUND_ZERO_RECOVERY,
  KRIS_HAMMER_THROW_PROJ_DEF,
  PRIYA_COMMAND_TABLE,
  PRIYA_TIGER_KNEE_ID,
  PRIYA_JAGUAR_DIVE_ID,
  PRIYA_RISING_FURY_ID,
  GHOST_COMMAND_TABLE,
  GHOST_BLADE_REKKA_ID,
  GHOST_SHADOW_STEP_ID,
  GHOST_FLASHBANG_ID,
  GHOST_SHADOW_STEP_RECOVERY,
  GHOST_SHADOW_STEP_IFRAMES,
  GHOST_FLASHBANG_PROJ_DEF,
  PRAETOR_COMMAND_TABLE,
  PRAETOR_SHIELD_BASH_ID,
  PRAETOR_BATON_SHOCK_ID,
  HALLOWAY_COMMAND_TABLE,
  HALLOWAY_BRIBE_TOSS_ID,
  HALLOWAY_COWER_DODGE_ID,
  HALLOWAY_COWER_DODGE_IFRAMES,
  HALLOWAY_COWER_DODGE_RECOVERY,
  HALLOWAY_BRIBE_TOSS_PROJ_DEF,
} from '@/data/commandSpecials';
import {
  RAY_TANK_BUSTER,
  ERROL_BOUNDARY_SMASH,
  ERROL_CREASE_SWEEP,
  KRIS_HAMMER_THROW,
  KRIS_GROUND_ZERO,
  PRIYA_TIGER_KNEE,
  PRIYA_JAGUAR_DIVE,
  PRIYA_RISING_FURY,
  GHOST_BLADE_REKKA,
  GHOST_SHADOW_STEP,
  GHOST_FLASHBANG,
  PRAETOR_SHIELD_BASH,
  PRAETOR_BATON_SHOCK,
  HALLOWAY_BRIBE_TOSS,
  HALLOWAY_COWER_DODGE,
} from '@/data/moves';
import { ProjectileManager } from '@/systems/ProjectileSystem';

const PLAYER_MAX_HP = 100;
const BASE_SPEED = 2.5;
const SPEED_PER_STAT = 0.25;
const ATTACK_BODY_H = 90;
const JUMP_POWER = 9;
/** Frames of stun after being forced off a destroyed vehicle. */
const DISMOUNT_STUN_FRAMES = 40;

/**
 * Player — input-driven Entity with a full light/heavy combo.
 *
 * LIGHT taps run the L1→L2→L3 string; HEAVY mid-string is the launcher
 * finisher; a cold HEAVY is standalone, or charged if held past the threshold.
 * Timing logic lives in the pure ComboTracker; this class wires input → moves
 * → hitboxes and handles per-attack recovery.
 */
export class Player extends Entity {
  readonly team: Team = 'player';

  private readonly inputMgr: InputManager;

  private readonly playerId: PlayerId;

  private combat?: CombatSystem;

  private readonly combo = new ComboTracker();

  private frame = 0;

  private recovery = 0;

  private heavyHeld = 0;

  /** Which attack clip the active move drives; read by syncAnim while attacking. */
  private attackAnimState: 'attackL' | 'attackH' = 'attackL';

  private readonly commandThrow: boolean;

  private readonly throwSpec: ThrowSpec;

  private readonly blockParry: BlockParryState = createBlockParryState();

  private stars: StarMeterState = createStarMeter();

  private readonly superDef: SuperDef;

  private greenHealth: GreenHealthState = createGreenHealthState();

  /** Currently mounted vehicle entity, or null when on foot. */
  private mountedVehicle: Vehicle | null = null;

  /** Pure vehicle state (HP, mount flag) owned while mounted. */
  private vehicleState: VehicleState | null = null;

  /** Definition of the active vehicle (speed, ram stats). */
  private vehicleDef: VehicleDef | null = null;

  /** Frames of stun remaining after a forced dismount (vehicle destroyed). */
  private mountStun = 0;

  /** Dodge-roll state: active window, i-frames, cooldown. */
  private readonly dodge: DodgeRollState = createDodgeRollState();

  /** Double-tap sprint state (separate from the invulnerable dodge-roll). */
  private readonly sprint: DashState = createDashState();

  /** SF-style motion-input ring buffer. */
  private readonly motionBuf = new MotionInputBuffer();

  /** Hero special move definitions (index 0 = forward special). */
  private readonly heroSpecials: readonly SpecialDef[];

  /**
   * When true, QCF / DP / charge + SPECIAL trigger the hero forward special
   * as an alternative to direction+SPECIAL (MY-2419). Toggled from options.
   */
  motionInputEnabled = false;

  /** Player-side taunt state machine (MY-1959). */
  private readonly tauntSystem = new TauntSystem();

  /** True while a taunt is actively playing; forwarded to syncAnim. */
  private isTaunting = false;

  /**
   * Hero-specific command-special table (MY-2438).
   * Null when the hero has no command-specials defined.
   * Keyed by heroId, resolved once in the constructor.
   */
  private readonly commandSpecialTable: CommandTable | null;

  /**
   * ProjectileManager wired by StageScene (MY-2438).
   * Only present once attachProjectileManager() is called; avoids coupling
   * the Player constructor to Phaser scene internals.
   */
  private projectileManager?: ProjectileManager;

  /** Called by StageScene to find the nearest world weapon for pickup. */
  private pickupQuery?: (x: number) => { def: WeaponDef; remove: () => void } | null;

  /** Reusable hit callback — avoids a closure allocation per attack swing. */
  private readonly onHitCallback = (): void => {
    const restore = notifyHitLanded(this.greenHealth);
    if (restore > 0) this.health.heal(restore);
  };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    hero: HeroDef,
    input: InputManager,
    playerId: PlayerId = 1,
  ) {
    super(scene, x, y, {
      maxHp: PLAYER_MAX_HP,
      color: hero.color,
      speed: BASE_SPEED + hero.stats.speed * SPEED_PER_STAT,
      label: hero.id.toUpperCase(),
      assetId: hero.assetId,
      poseAssetIds: hero.poseAssetIds,
    });
    this.inputMgr = input;
    this.playerId = playerId;
    this.commandThrow = hero.commandThrow ?? false;
    this.throwSpec = this.commandThrow ? COMMAND_THROW_SPEC : DEFAULT_THROW_SPEC;
    this.superDef = getHeroSuper(hero.id);
    this.heroSpecials = getHeroSpecials(hero.id);

    // Per-hero command-special tables (MY-2438 / MY-2418). Add new heroes here as data.
    const COMMAND_TABLES: Record<string, CommandTable> = {
      ray: RAY_COMMAND_TABLE,
      errol: ERROL_COMMAND_TABLE,
      kris: KRIS_COMMAND_TABLE,
      priya: PRIYA_COMMAND_TABLE,
      ghost: GHOST_COMMAND_TABLE,
      praetor: PRAETOR_COMMAND_TABLE,
      halloway: HALLOWAY_COMMAND_TABLE,
    };
    this.commandSpecialTable = COMMAND_TABLES[hero.id] ?? null;
  }

  /** Current star count (read by StageScene for HUD display). */
  get starCount(): number {
    return this.stars.count;
  }

  /** HP currently shown as green (recoverable). 0 = no active window. */
  get greenHpAmount(): number {
    return this.greenHealth.greenAmount;
  }

  attachCombat(combat: CombatSystem): void {
    this.combat = combat;
  }

  /** Register a function that returns the nearest world weapon within pickup range. */
  attachPickupSource(fn: (x: number) => { def: WeaponDef; remove: () => void } | null): void {
    this.pickupQuery = fn;
  }

  /**
   * Wire a ProjectileManager so command-specials that fire projectiles can
   * spawn them (MY-2438). Called by StageScene after creating the manager,
   * analogous to attachCombat().
   */
  attachProjectileManager(pm: ProjectileManager): void {
    this.projectileManager = pm;
  }

  /**
   * Mount a vehicle. Suspends normal moveset and enters vehicle control mode.
   * No-op if already mounted, KO'd, or in post-dismount stun.
   * Called by StageScene when the player presses PICKUP near a mountable vehicle.
   */
  mount(vehicle: Vehicle, def: VehicleDef): void {
    if (this.mountedVehicle || this.isKO || this.mountStun > 0) return;
    this.mountedVehicle = vehicle;
    this.vehicleDef = def;
    this.vehicleState = mountVehicle(createVehicleState(def));
    vehicle.isMounted = true;
  }

  /** Override: redirect hits to vehicle HP while mounted; normal block/parry otherwise. */
  override receiveHit(spec: HitSpec, fromCenterX: number): void {
    if (this.isKO) return;

    // Dodge-roll i-frames: absorb the hit completely (no damage, no hitstun).
    if (isDodgeInvulnerable(this.dodge)) return;

    // A hit cancels any active taunt and forfeits remaining reward.
    if (this.isTaunting) {
      this.tauntSystem.cancel();
      this.isTaunting = false;
    }

    // While mounted, the vehicle absorbs damage instead of the player.
    if (this.mountedVehicle !== null && this.vehicleState !== null) {
      const vs = this.vehicleState;
      const { nextState, justDestroyed } = vehicleReceiveHit(vs, spec.damage);
      this.vehicleState = nextState;
      if (justDestroyed) {
        this.forceVehicleDismount();
      }
      return;
    }

    const { damage, hitstun, knockback, parried } = resolveBlock(
      this.blockParry,
      spec.damage,
      spec.hitstun,
      spec.knockback,
      this.frame,
    );
    if (parried) {
      this.onParry();
      return;
    }
    if (this.blockParry.blocking) {
      this.scene.events.emit('player-block');
    }
    // Taking damage locks in any pending green HP as permanent loss.
    lockInGreenLoss(this.greenHealth);
    cancelDash(this.sprint);
    const dir = this.x >= fromCenterX ? 1 : -1;
    this.takeDamage(damage, dir * knockback);
    this.hitstun = Math.max(this.hitstun, hitstun);
  }

  update(): void {
    const wasAirborne = this.isAirborne;
    this.frame += 1;
    this.tickHitstun();
    this.tickJump();
    if (wasAirborne && !this.isAirborne && !this.isKO) {
      this.scene.events.emit('player-land');
    }
    tickBlockParry(this.blockParry, this.frame);
    tickGreenHealth(this.greenHealth);
    tickDodgeRoll(this.dodge);
    // Tick taunt cooldown every frame regardless of state.
    this.tauntSystem.tickCooldown();
    if (this.tickThrown()) return; // in flight — no input
    if (this.isKO) return;
    if (this.recovery > 0) this.recovery -= 1;
    // Hitstun cancels an active taunt and clears the flag.
    if (this.isHitstunned) {
      if (this.isTaunting) {
        this.tauntSystem.cancel();
        this.isTaunting = false;
      }
      // A hit interrupts any HEAVY charge so releasing HEAVY afterwards can't fire
      // a phantom charged attack.
      this.heavyHeld = 0;
      // Drive the anim layer while hitstunned so the SM flips to 'hurt' (syncAnim
      // reads hurt:isHitstunned), which releases a one-shot super/special lock and
      // decrements it — otherwise getting hit mid-super shows no flinch and the
      // lock stalls for the whole hitstun window.
      this.syncAnim({ moving: false });
      return;
    }

    // Brief stun after forced dismount (vehicle destroyed under player).
    if (this.mountStun > 0) {
      this.mountStun -= 1;
      this.syncAnim({ moving: false });
      return;
    }

    // Vehicle control mode — normal moveset suspended while mounted.
    if (this.mountedVehicle) {
      this.handleVehicleInput();
      return;
    }

    // Active dodge-roll: apply directional movement and skip all other input.
    if (this.dodge.active) {
      let dx = this.dodge.dirX;
      let dy = this.dodge.dirY;
      if (dx !== 0 && dy !== 0) {
        const inv = 1 / Math.SQRT2;
        dx *= inv;
        dy *= inv;
      }
      this.x += dx * DODGE_SPEED;
      this.y += dy * DODGE_SPEED;
      this.x = Phaser.Math.Clamp(this.x, 40, this.worldWidth - 40);
      this.y = Phaser.Math.Clamp(this.y, 160, this.scene.scale.height - 100);
      this.syncAnim({ moving: true });
      return;
    }

    // Jump (ground plane keeps moving; z is visual height).
    if (this.inputMgr.justDown(this.playerId, 'JUMP')) {
      const preJump = this.isAirborne;
      this.startJump(JUMP_POWER);
      if (!preJump && this.isAirborne) this.scene.events.emit('player-jump');
    }

    const i = this.inputMgr;
    const p = this.playerId;
    const s = this.scene;

    // Track directional history every frame for SF-style motion recognition.
    this.motionBuf.push(
      dirsToDir(
        i.isDown(s, p, 'LEFT'),
        i.isDown(s, p, 'RIGHT'),
        i.isDown(s, p, 'UP'),
        i.isDown(s, p, 'DOWN'),
      ),
    );

    // Block / parry input — tap = parry attempt, hold = block stance.
    if (i.justDown(p, 'BLOCK')) notifyBlockTap(this.blockParry, this.frame);
    this.blockParry.blocking = i.isDown(s, p, 'BLOCK');

    // Movement is locked during attack recovery so swings commit.
    let moved = false;
    if (this.recovery <= 0) {
      let dx = 0;
      let dy = 0;
      if (i.isDown(s, p, 'LEFT')) dx -= 1;
      if (i.isDown(s, p, 'RIGHT')) dx += 1;
      if (i.isDown(s, p, 'UP')) dy -= 1;
      if (i.isDown(s, p, 'DOWN')) dy += 1;

      if (dx !== 0) this.setFacing(dx > 0 ? 1 : -1);
      if (dx !== 0 && dy !== 0) {
        const inv = 1 / Math.SQRT2;
        dx *= inv;
        dy *= inv;
      }
      this.x += dx * this.speed;
      this.y += dy * this.speed;
      this.x = Phaser.Math.Clamp(this.x, 40, this.worldWidth - 40);
      this.y = Phaser.Math.Clamp(this.y, 160, this.scene.scale.height - 100);
      moved = dx !== 0 || dy !== 0;
    }

    // Dodge roll: SHIFT (P1) / NUMPAD_7 (P2) / L2 gamepad — not during hitstun/throw/air/active.
    if (
      !this.isAirborne &&
      canDodge(this.dodge) &&
      this.inputMgr.justDown(this.playerId, 'DODGE')
    ) {
      this.startDodgeRoll();
      return;
    }

    // Double-tap sprint: L/L or R/R within DASH_WINDOW_FRAMES → run state + dash attacks.
    tickDash(this.sprint, i.justDown(p, 'LEFT'), i.justDown(p, 'RIGHT'), this.frame);

    // Taunt input: TAUNT key when grounded + idle (not in recovery, not airborne, not blocking).
    if (
      this.inputMgr.justDown(this.playerId, 'TAUNT') &&
      this.tauntSystem.canStart(!this.isAirborne, this.recovery > 0 || this.blockParry.blocking)
    ) {
      this.tauntSystem.start();
      this.isTaunting = true;
    }

    // Tick active taunt: an attack/dodge/jump during a taunt interrupts it.
    if (this.isTaunting) {
      const interrupted = this.isAirborne || this.recovery > 0;
      if (interrupted) {
        this.tauntSystem.cancel();
        this.isTaunting = false;
      } else {
        const result = this.tauntSystem.tick(PLAYER_MAX_HP);
        if (result.healDelta > 0) this.heal(result.healDelta);
        for (let s = 0; s < result.starDelta; s++) addStar(this.stars);
        if (!result.stillActive) this.isTaunting = false;
      }
    }

    this.handlePickup();
    this.handleAttacks();
    // Any attack (recovery > 0) interrupts an ongoing taunt.
    if (this.isTaunting && this.recovery > 0) {
      this.tauntSystem.cancel();
      this.isTaunting = false;
    }
    this.syncAnim({
      moving: moved,
      running: this.sprint.active,
      attacking: this.recovery > 0,
      attackState: this.attackAnimState,
      blocking: this.blockParry.blocking,
      taunting: this.isTaunting,
    });
  }

  /**
   * Handle the PICKUP button: pick up a nearby world weapon when unarmed,
   * or throw the held weapon as a ranged attack.
   */
  private handlePickup(): void {
    if (this.recovery > 0) return;
    if (!this.inputMgr.justDown(this.playerId, 'PICKUP')) return;
    if (this.heldWeapon) {
      this.throwWeapon();
    } else {
      const nearby = this.pickupQuery?.(this.x);
      if (nearby) {
        this.equipWeapon(createWeaponState(nearby.def));
        nearby.remove();
        this.scene.events.emit('player-pickup');
      }
    }
  }

  /** Throw the held weapon forward as a ranged hitbox, then break it. */
  private throwWeapon(): void {
    const weapon = this.heldWeapon;
    if (!weapon || !this.combat) return;
    const throwSpec: HitSpec = {
      damage: applyWeaponDamage(10, weapon),
      knockback: 32,
      hitstun: 22,
      weaponId: weapon.def.id,
    };
    const hitX = this.x + this.facing * WEAPON_THROW_RANGE;
    const box = boxFromCenter(hitX, this.y, 40, ATTACK_BODY_H);
    this.combat.spawn(new Hitbox(this, box, throwSpec, 6));
    this.recovery = 25;
    // Cosmetic in-flight visual (StageScene renders it; gameplay hit is the
    // instant hitbox above). Flies from the hand to where the weapon lands.
    this.scene.events.emit('weapon-thrown', {
      fromX: this.x + this.facing * 18,
      fromY: this.y - 24,
      toX: hitX,
      toY: this.y - 8,
      def: weapon.def,
    });
    this.scene.events.emit('weapon-dropped', { x: hitX, y: this.y, def: weapon.def });
    this.dropWeapon();
  }

  private handleAttacks(): void {
    // Cannot attack while in block stance (but CAN attack during counter window).
    if (this.blockParry.blocking) return;

    const i = this.inputMgr;
    const p = this.playerId;
    const s = this.scene;

    // Heavy is charge-on-hold: count while held, fire on release.
    if (i.isDown(s, p, 'HEAVY')) this.heavyHeld += 1;

    if (this.recovery > 0) return;

    // SPECIAL + UP = consume one star → screen-clear super.
    if (i.justDown(p, 'SPECIAL') && i.isDown(s, p, 'UP')) {
      if (consumeStar(this.stars)) {
        this.activateSuper();
        return;
      }
    }

    // SF-style motion inputs (QCF / DP / charge) + SPECIAL → hero forward special.
    // Only active when the player has opted-in via options (motionInputEnabled).
    if (this.motionInputEnabled && i.justDown(p, 'SPECIAL')) {
      const facingRight = this.facing > 0;
      if (
        this.motionBuf.tryConsumeQcf(facingRight) ||
        this.motionBuf.tryConsumeDP(facingRight) ||
        this.motionBuf.tryConsumeCharge(facingRight)
      ) {
        const fwdSpecial = this.heroSpecials[0];
        if (fwdSpecial) {
          this.heavyHeld = 0; // committing a special cancels any in-progress HEAVY charge
          this.scene.events.emit('player-special', {
            id: fwdSpecial.id,
            animKey: fwdSpecial.animKey,
          });
          this.recovery = 35;
          // Play the bespoke forward-special clip if its sheet shipped (e.g. ray
          // glasgowKiss / errol ironLion / priya cobraStrike / ghost stimShot).
          // Use def.id — playSheetState already prepends anim_<hero>_special_.
          this.playSheetState('special_' + fwdSpecial.id);
          return;
        }
      }
    }

    // Command-specials: direction + SPECIAL for hero-specific moves.
    // Data-driven: each hero with a commandSpecialTable is handled here (MY-2438).
    // Priority: super (UP+SPECIAL) and motion-inputs fire first (see above).
    if (i.justDown(p, 'SPECIAL') && this.commandSpecialTable !== null) {
      const dx = i.isDown(s, p, 'LEFT') ? -1 : i.isDown(s, p, 'RIGHT') ? 1 : 0;
      const cmdId = resolveCommandSpecial(
        {
          facing: this.facing,
          held: {
            up: i.isDown(s, p, 'UP'),
            down: i.isDown(s, p, 'DOWN'),
            left: dx < 0,
            right: dx > 0,
          },
          dashActive: this.sprint.active,
          airborne: this.isAirborne,
        },
        this.commandSpecialTable,
      );

      if (cmdId !== null) {
        cancelDash(this.sprint);
        this.heavyHeld = 0; // committing a command-special cancels any HEAVY charge
        if (this.dispatchCommandSpecial(cmdId)) return;
      }
    }

    // Jump attack — LIGHT while airborne bypasses the ground combo.
    if (this.isAirborne && i.justDown(p, 'LIGHT')) {
      this.performMove(JUMP_ATTACK);
      return;
    }

    if (i.justDown(p, 'LIGHT')) {
      const dx = i.isDown(s, p, 'LEFT') ? -1 : i.isDown(s, p, 'RIGHT') ? 1 : 0;
      const ctx = resolveAttackContext(false, this.sprint.active, this.facing, dx);

      // Grab check applies in both normal and dash contexts (run→grab).
      if (ctx === 'normal' || ctx === 'dash') {
        const target = this.combat?.findGrabTarget(
          this.x,
          this.team,
          this.throwSpec.grabRange,
          this.commandThrow,
        );
        if (target) {
          cancelDash(this.sprint);
          this.performThrow(target);
          return;
        }
      }

      if (ctx === 'dash') {
        cancelDash(this.sprint);
        this.performMove(DASH_ATTACK);
        return;
      }

      if (ctx === 'back') {
        this.performMove(BACK_ATTACK);
        return;
      }

      // Normal ground light combo.
      this.performMove(this.combo.press('L', this.frame));
      return;
    }

    if (i.justUp(p, 'HEAVY')) {
      const charged = this.heavyHeld >= HEAVY_CHARGE_FRAMES;
      this.heavyHeld = 0;
      this.performMove(this.combo.press('H', this.frame, charged));
    }
  }

  /**
   * Dispatch a resolved command-special ID to the appropriate execution path.
   * Returns true when the move was handled (caller should return from handleAttacks).
   * Returns false for unknown IDs (graceful no-op; new heroes can add IDs here).
   *
   * MY-2438: Ray and Errol handled. Future heroes add their IDs below.
   */
  private dispatchCommandSpecial(cmdId: string): boolean {
    // ── RAY "TANK" MILLER ───────────────────────────────────────────────────────

    if (cmdId === 'rayTankBuster') {
      this.performMove(RAY_TANK_BUSTER);
      this.playSheetState('special_tankBuster');
      this.scene.events.emit('player-special', {
        id: 'rayTankBuster',
        animKey: 'special_ray_tankBuster',
      });
      return true;
    }

    if (cmdId === RAY_PILEDRIVER_ID) {
      const target = this.combat?.findGrabTarget(
        this.x,
        this.team,
        COMMAND_THROW_SPEC.grabRange,
        true,
      );
      if (target) {
        this.performThrow(target);
        this.recovery = RAY_PILEDRIVER_HIT_RECOVERY;
        this.playSheetState('special_spinningPiledriver');
        this.scene.events.emit('player-special', {
          id: RAY_PILEDRIVER_ID,
          animKey: 'special_ray_spinningPiledriver',
        });
      } else {
        // Whiff: no target in grab range — apply short recovery.
        this.recovery = RAY_PILEDRIVER_WHIFF_RECOVERY;
        this.attackAnimState = 'attackH';
        this.playSheetState('special_spinningPiledriver');
        this.scene.events.emit('player-special', {
          id: RAY_PILEDRIVER_ID,
          animKey: 'special_ray_spinningPiledriver',
        });
      }
      return true;
    }

    // ── ERROL "DREAD" CLARKE ────────────────────────────────────────────────────

    if (cmdId === ERROL_BOUNDARY_SMASH_ID) {
      // Boundary Smash: spawn a horizontal projectile via ProjectileManager.
      // Recovery still applies so Errol commits to the throw.
      this.recovery = ERROL_BOUNDARY_SMASH.recovery;
      this.attackAnimState = ERROL_BOUNDARY_SMASH.attackState;
      this.playSheetState('special_boundarySmash');
      if (this.projectileManager) {
        // Spawn slightly ahead of Errol so the hitbox doesn't overlap him.
        const spawnX = this.x + this.facing * 20;
        this.projectileManager.spawn(
          this,
          spawnX,
          this.y,
          this.facing as 1 | -1,
          ERROL_BOUNDARY_SMASH_PROJ_DEF,
        );
      }
      this.scene.events.emit('player-special', {
        id: ERROL_BOUNDARY_SMASH_ID,
        animKey: 'anim_errol_attackh',
      });
      return true;
    }

    if (cmdId === ERROL_CREASE_SWEEP_ID) {
      // Crease Sweep: 360° AoE — spawn two symmetric hitboxes (forward + back).
      // Both share the same spec; the dual spawn gives the "both sides" coverage.
      if (this.combat) {
        const sweepSpec = { ...ERROL_CREASE_SWEEP.spec, sfxTag: ERROL_CREASE_SWEEP_ID };
        const fwdBox = boxFromCenter(
          this.x + this.facing * ERROL_CREASE_SWEEP.reach,
          this.y,
          55,
          90,
        );
        const backBox = boxFromCenter(
          this.x - this.facing * ERROL_CREASE_SWEEP.reach,
          this.y,
          55,
          90,
        );
        this.combat.spawn(new Hitbox(this, fwdBox, sweepSpec, ERROL_CREASE_SWEEP.activeFrames));
        this.combat.spawn(new Hitbox(this, backBox, sweepSpec, ERROL_CREASE_SWEEP.activeFrames));
      }
      this.recovery = ERROL_CREASE_SWEEP_RECOVERY;
      this.attackAnimState = ERROL_CREASE_SWEEP.attackState;
      this.playSheetState('special_creaseSweep');
      this.scene.events.emit('player-special', {
        id: ERROL_CREASE_SWEEP_ID,
        animKey: 'anim_errol_spin',
      });
      return true;
    }

    // ── KRIS "HAMMER" VANCE ─────────────────────────────────────────────────────

    if (cmdId === KRIS_HAMMER_THROW_ID) {
      // Hammer Throw: boomerang projectile via ProjectileManager.
      this.recovery = KRIS_HAMMER_THROW.recovery;
      this.attackAnimState = KRIS_HAMMER_THROW.attackState;
      this.playSheetState('special_hammerThrow');
      if (this.projectileManager) {
        const spawnX = this.x + this.facing * 20;
        this.projectileManager.spawn(
          this,
          spawnX,
          this.y,
          this.facing as 1 | -1,
          KRIS_HAMMER_THROW_PROJ_DEF,
        );
      }
      this.scene.events.emit('player-special', {
        id: KRIS_HAMMER_THROW_ID,
        animKey: 'anim_kris_attackh',
      });
      return true;
    }

    if (cmdId === KRIS_GROUND_ZERO_ID) {
      // Ground Zero: overhead slam → wide AoE hitbox in front and slightly behind.
      if (this.combat) {
        const groundSpec = { ...KRIS_GROUND_ZERO.spec, sfxTag: KRIS_GROUND_ZERO_ID };
        const impactBox = boxFromCenter(
          this.x + this.facing * KRIS_GROUND_ZERO.reach,
          this.y,
          80,
          90,
        );
        this.combat.spawn(new Hitbox(this, impactBox, groundSpec, KRIS_GROUND_ZERO.activeFrames));
      }
      this.recovery = KRIS_GROUND_ZERO_RECOVERY;
      this.attackAnimState = KRIS_GROUND_ZERO.attackState;
      this.playSheetState('special_groundZero');
      this.scene.events.emit('player-special', {
        id: KRIS_GROUND_ZERO_ID,
        animKey: 'anim_kris_attackh',
      });
      return true;
    }

    // ── PRIYA "JAGUAR" HASSAN ───────────────────────────────────────────────────

    if (cmdId === PRIYA_TIGER_KNEE_ID) {
      // Tiger Knee: rising knee launcher — reuses performMove for hitbox + recovery.
      this.performMove(PRIYA_TIGER_KNEE);
      this.playSheetState('special_tigerKnee');
      this.scene.events.emit('player-special', {
        id: PRIYA_TIGER_KNEE_ID,
        animKey: 'anim_priya_attackh',
      });
      return true;
    }

    if (cmdId === PRIYA_JAGUAR_DIVE_ID) {
      // Jaguar Dive: downward dive-kick (only fires when airborne — table has 'air').
      this.performMove(PRIYA_JAGUAR_DIVE);
      this.playSheetState('special_jaguarDive');
      this.scene.events.emit('player-special', {
        id: PRIYA_JAGUAR_DIVE_ID,
        animKey: 'anim_priya_attackh',
      });
      return true;
    }

    if (cmdId === PRIYA_RISING_FURY_ID) {
      // Rising Fury: flash-kick anti-air launcher.
      this.performMove(PRIYA_RISING_FURY);
      this.playSheetState('special_risingFury');
      this.scene.events.emit('player-special', {
        id: PRIYA_RISING_FURY_ID,
        animKey: 'anim_priya_attackh',
      });
      return true;
    }

    // ── GHOST "BLADE" CHEN ─────────────────────────────────────────────────────

    if (cmdId === GHOST_BLADE_REKKA_ID) {
      // Blade Rekka: multi-hit knife flurry — wide active frames spawn one hitbox
      // covering the full flurry window. Ghost closes distance slightly.
      if (this.combat) {
        const rekkaSpec = { ...GHOST_BLADE_REKKA.spec, sfxTag: GHOST_BLADE_REKKA_ID };
        const rekkaBox = boxFromCenter(
          this.x + this.facing * GHOST_BLADE_REKKA.reach,
          this.y,
          44,
          90,
        );
        this.combat.spawn(new Hitbox(this, rekkaBox, rekkaSpec, GHOST_BLADE_REKKA.activeFrames));
      }
      this.recovery = GHOST_BLADE_REKKA.recovery;
      this.attackAnimState = GHOST_BLADE_REKKA.attackState;
      this.playSheetState('special_bladeRekka');
      this.scene.events.emit('player-special', {
        id: GHOST_BLADE_REKKA_ID,
        animKey: 'anim_ghost_attackl',
      });
      return true;
    }

    if (cmdId === GHOST_SHADOW_STEP_ID) {
      // Shadow Step: short backward reposition with brief i-frames. No hitbox.
      // Moves Ghost backward 60 px (opposite facing direction) then activates
      // the dodge-roll i-frame window (GHOST_SHADOW_STEP_IFRAMES frames) so
      // incoming hits are absorbed during the reposition.  We set framesLeft to
      // a value that makes isDodgeInvulnerable() return true for the desired
      // window: framesLeft must be > (DODGE_TOTAL_FRAMES - DODGE_IFRAME_FRAMES).
      this.x -= this.facing * 60;
      this.recovery = GHOST_SHADOW_STEP_RECOVERY;
      this.attackAnimState = GHOST_SHADOW_STEP.attackState;
      this.playSheetState('special_shadowStep');
      // Activate dodge state for i-frame window without triggering movement speed.
      beginDodgeRoll(this.dodge, -this.facing, 0);
      // Clamp framesLeft so the window expires after GHOST_SHADOW_STEP_IFRAMES.
      // isDodgeInvulnerable = framesLeft > (DODGE_TOTAL_FRAMES - DODGE_IFRAME_FRAMES) = 15.
      // Setting framesLeft = GHOST_SHADOW_STEP_IFRAMES + (DODGE_TOTAL_FRAMES - DODGE_IFRAME_FRAMES)
      // means it stays invulnerable for exactly GHOST_SHADOW_STEP_IFRAMES ticks.
      this.dodge.framesLeft = GHOST_SHADOW_STEP_IFRAMES + (26 - 11); // DODGE_TOTAL_FRAMES - DODGE_IFRAME_FRAMES = 15
      this.scene.events.emit('player-special', {
        id: GHOST_SHADOW_STEP_ID,
        animKey: 'anim_ghost_attackl',
      });
      return true;
    }

    if (cmdId === GHOST_FLASHBANG_ID) {
      // Flashbang: stun projectile via ProjectileManager.
      this.recovery = GHOST_FLASHBANG.recovery;
      this.attackAnimState = GHOST_FLASHBANG.attackState;
      // Bespoke flashbang clip — reachable in default play (down+SPECIAL).
      this.playSheetState('special_flashbang');
      if (this.projectileManager) {
        const spawnX = this.x + this.facing * 20;
        this.projectileManager.spawn(
          this,
          spawnX,
          this.y,
          this.facing as 1 | -1,
          GHOST_FLASHBANG_PROJ_DEF,
        );
      }
      this.scene.events.emit('player-special', {
        id: GHOST_FLASHBANG_ID,
        animKey: 'anim_ghost_attackl',
      });
      return true;
    }

    // ── THE PRAETOR ────────────────────────────────────────────────────────────

    if (cmdId === PRAETOR_SHIELD_BASH_ID) {
      this.performMove(PRAETOR_SHIELD_BASH);
      this.playSheetState('special');
      this.scene.events.emit('player-special', {
        id: PRAETOR_SHIELD_BASH_ID,
        animKey: 'anim_the-praetor_special',
      });
      return true;
    }

    if (cmdId === PRAETOR_BATON_SHOCK_ID) {
      this.performMove(PRAETOR_BATON_SHOCK);
      this.playSheetState('attackL');
      this.scene.events.emit('player-special', {
        id: PRAETOR_BATON_SHOCK_ID,
        animKey: 'anim_the-praetor_attackL',
      });
      return true;
    }

    // ── THE RT HON. HALLOWAY ──────────────────────────────────────────────────

    if (cmdId === HALLOWAY_BRIBE_TOSS_ID) {
      this.recovery = HALLOWAY_BRIBE_TOSS.recovery;
      this.attackAnimState = HALLOWAY_BRIBE_TOSS.attackState;
      this.playSheetState('attackH');
      if (this.projectileManager) {
        const spawnX = this.x + this.facing * 20;
        this.projectileManager.spawn(
          this,
          spawnX,
          this.y,
          this.facing as 1 | -1,
          HALLOWAY_BRIBE_TOSS_PROJ_DEF,
        );
      }
      this.scene.events.emit('player-special', {
        id: HALLOWAY_BRIBE_TOSS_ID,
        animKey: 'anim_pm-halloway_attackH',
      });
      return true;
    }

    if (cmdId === HALLOWAY_COWER_DODGE_ID) {
      this.x -= this.facing * 44;
      this.recovery = HALLOWAY_COWER_DODGE_RECOVERY;
      this.attackAnimState = HALLOWAY_COWER_DODGE.attackState;
      this.playSheetState('hurt');
      beginDodgeRoll(this.dodge, -this.facing, 0);
      this.dodge.framesLeft = HALLOWAY_COWER_DODGE_IFRAMES + (26 - 11);
      this.scene.events.emit('player-special', {
        id: HALLOWAY_COWER_DODGE_ID,
        animKey: 'anim_pm-halloway_hurt',
      });
      return true;
    }

    return false;
  }

  /** Activate the hero's super: costs HP (shows green), screen-clear, 8-frame freeze. */
  private activateSuper(): void {
    if (!this.combat) return;
    this.heavyHeld = 0; // committing the super cancels any in-progress HEAVY charge
    // HP cost first; the spent amount becomes green (recoverable by follow-up hits).
    // Clamp so the super can NEVER self-KO: the caller already spent a star, and a
    // meter-funded signature move that suicides the player (wasting the star and a
    // life) is a bug. At very low HP it simply costs less and leaves the hero on 1 HP.
    const cost = superSelfDamageCost(this.health.current, SPECIAL_HP_COST);
    this.takeDamage(cost);
    beginGreenWindow(this.greenHealth, cost);
    this.combat.resolveScreenClear(this, this.superDef.radius, superToHitSpec(this.superDef));
    this.combat.startHitpause(SUPER_FREEZE_FRAMES);
    this.recovery = 40;
    // Play the bespoke per-hero super clip if its sheet shipped (anim_<hero>_super);
    // otherwise the recovery-driven attack pose still renders (graceful fallback).
    this.playSheetState('super');
    this.scene.events.emit('player-super', {
      x: this.x,
      animKey: this.superDef.animKey,
      starsLeft: this.stars.count,
    });
  }

  /**
   * Execute a throw on `target`. Direction is read from current d-pad state:
   * - up = overhead, opposite-to-facing = back throw, else = forward (default).
   */
  private performThrow(target: Combatant): void {
    this.heavyHeld = 0; // committing a throw cancels any in-progress HEAVY charge
    const i = this.inputMgr;
    const p = this.playerId;
    const s = this.scene;
    const dx = i.isDown(s, p, 'LEFT') ? -1 : i.isDown(s, p, 'RIGHT') ? 1 : 0;
    const dy = i.isDown(s, p, 'UP') ? -1 : i.isDown(s, p, 'DOWN') ? 1 : 0;
    const throwType = resolveThrowType(this.facing, dx, dy);
    const state = createThrownState(throwType, this.facing, this.throwSpec, this.team);
    target.beginThrow(state);
    this.recovery = 30;
    this.combo.reset();
    this.scene.events.emit('player-throw');
  }

  private performMove(move: MoveDef): void {
    // Parry counter bonus first, then weapon multiplier on top.
    let baseDamage = isInCounterWindow(this.blockParry)
      ? applyCounterBonus(move.spec.damage)
      : move.spec.damage;
    let reach = move.reach;

    const weapon = this.heldWeapon;
    if (weapon) {
      baseDamage = applyWeaponDamage(baseDamage, weapon);
      reach = effectiveReach(reach, weapon);
    }

    const spec: HitSpec = {
      ...move.spec,
      damage: baseDamage,
      ...(move.launcher ? { launcher: true as const } : {}),
      sfxTag: move.id,
      ...(weapon ? { weaponId: weapon.def.id } : {}),
    };

    this.recovery = move.recovery;
    // A normal move cancels any in-progress HEAVY charge (so it can't fire a phantom
    // charged attack on release) and clears any residual super/special one-shot lock
    // (otherwise applyPoseTexture keeps the old super clip while this hitbox is live).
    this.heavyHeld = 0;
    this.oneShotAnimLock = 0;
    this.attackAnimState = move.attackState;
    this.scene.tweens.add({
      targets: this.bodyImage ?? this.bodyRect,
      scaleY: { from: 1, to: move.launcher ? 1.12 : 0.92 },
      duration: 60,
      yoyo: true,
    });
    if (!this.combat) return;
    const box = boxFromCenter(this.x + this.facing * reach, this.y, 55, ATTACK_BODY_H);
    this.combat.spawn(new Hitbox(this, box, spec, move.activeFrames, this.onHitCallback));

    // Consume one durability per swing; drop weapon when it breaks.
    if (weapon) {
      const broke = useWeapon(weapon);
      if (broke) {
        this.scene.events.emit('weapon-dropped', { x: this.x, y: this.y, def: weapon.def });
        this.dropWeapon();
      }
    }
  }

  /** Initiate a dodge roll in the current movement direction (or facing if idle). */
  private startDodgeRoll(): void {
    const i = this.inputMgr;
    const p = this.playerId;
    const s = this.scene;
    const dirX = i.isDown(s, p, 'LEFT') ? -1 : i.isDown(s, p, 'RIGHT') ? 1 : this.facing;
    const dirY = i.isDown(s, p, 'UP') ? -1 : i.isDown(s, p, 'DOWN') ? 1 : 0;
    beginDodgeRoll(this.dodge, dirX, dirY);
    this.scene.events.emit('player-dodge');
  }

  /** Vehicle movement/attack mode. Called each frame while mounted. */
  private handleVehicleInput(): void {
    if (!this.mountedVehicle || !this.vehicleDef) return;

    const i = this.inputMgr;
    const p = this.playerId;
    const s = this.scene;

    // SPECIAL = voluntary dismount (no stun).
    if (i.justDown(p, 'SPECIAL')) {
      this.mountedVehicle.isMounted = false;
      this.mountedVehicle = null;
      this.vehicleState = null;
      this.vehicleDef = null;
      return;
    }

    let dx = 0;
    let dy = 0;
    if (i.isDown(s, p, 'LEFT')) dx -= 1;
    if (i.isDown(s, p, 'RIGHT')) dx += 1;
    if (i.isDown(s, p, 'UP')) dy -= 1;
    if (i.isDown(s, p, 'DOWN')) dy += 1;

    if (dx !== 0) this.setFacing(dx > 0 ? 1 : -1);
    if (dx !== 0 && dy !== 0) {
      const inv = 1 / Math.SQRT2;
      dx *= inv;
      dy *= inv;
    }

    const vSpeed = this.speed * this.vehicleDef.speedMult;
    this.x += dx * vSpeed;
    this.y += dy * vSpeed;
    this.x = Phaser.Math.Clamp(this.x, 40, this.worldWidth - 40);
    this.y = Phaser.Math.Clamp(this.y, 160, this.scene.scale.height - 100);

    // Vehicle moves with the player.
    this.mountedVehicle.x = this.x;
    this.mountedVehicle.y = this.y;

    // LIGHT = RAM attack.
    if (i.justDown(p, 'LIGHT')) {
      this.spawnRamHitbox();
    }

    const moved = dx !== 0 || dy !== 0;
    this.syncAnim({ moving: moved });
  }

  /** Spawn a forward ram hitbox using vehicle stats. */
  private spawnRamHitbox(): void {
    if (!this.vehicleDef || !this.combat) return;
    const ramSpec: HitSpec = {
      damage: this.vehicleDef.ramDamage,
      knockback: this.vehicleDef.ramKnockback,
      hitstun: this.vehicleDef.ramHitstun,
    };
    const box = boxFromCenter(
      this.x + this.facing * this.vehicleDef.ramRadius,
      this.y,
      this.vehicleDef.ramRadius * 2,
      ATTACK_BODY_H,
    );
    this.combat.spawn(new Hitbox(this, box, ramSpec, 4));
  }

  /**
   * Forced dismount: vehicle HP hit 0. Applies brief stun and
   * notifies the Vehicle entity to update its visual.
   */
  private forceVehicleDismount(): void {
    if (this.mountedVehicle) {
      this.mountedVehicle.onDestroyed();
      this.mountedVehicle = null;
    }
    this.vehicleState = null;
    this.vehicleDef = null;
    this.mountStun = DISMOUNT_STUN_FRAMES;
  }

  /** Triggered when a parry succeeds: white flash + 4-frame screen freeze. */
  private onParry(): void {
    this.bodyRect.setFillStyle(0xffffff);
    this.scene.time.delayedCall(80, () => {
      if (!this.isKO) this.bodyRect.setFillStyle(this.bodyColor);
    });
    this.combat?.startHitpause(SCREEN_FREEZE_FRAMES);
    this.scene.events.emit('player-parry');
  }
}
